import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './use-auth'
import { usePremium } from './usePremium'
import { useAdmin } from './use-admin'

const STRIPE_CHECKOUT_URL = 'https://buy.stripe.com/fZe6rq7RdbAXeTC28N'
const TRIAL_DAYS = 30

interface TrialStatus {
  isTrialActive: boolean
  daysRemaining: number
  needsUpgrade: boolean
  loading: boolean
}

/**
 * Hook to enforce premium subscription after trial period
 * Redirects to Stripe checkout if user is past trial and not premium
 * Admin users are always bypassed
 */
export function useRequirePremium(options?: {
  redirectToCheckout?: boolean
  bypassForAdmin?: boolean
}) {
  const { user } = useAuth()
  const { isPremium, source, loading: premiumLoading } = usePremium()
  const { isAdmin, loading: adminLoading } = useAdmin()
  const navigate = useNavigate()
  const [status, setStatus] = useState<TrialStatus>({
    isTrialActive: true,
    daysRemaining: TRIAL_DAYS,
    needsUpgrade: false,
    loading: true
  })

  useEffect(() => {
    if (!user || premiumLoading || adminLoading) {
      return
    }

    checkTrialStatus()
  }, [user, isPremium, isAdmin, premiumLoading, adminLoading])

  async function checkTrialStatus() {
    // Admins never need to upgrade
    if (isAdmin) {
      setStatus({
        isTrialActive: true,
        daysRemaining: 999,
        needsUpgrade: false,
        loading: false
      })
      return
    }

    // Users with premium access (subscription or override) don't need trial
    if (isPremium) {
      setStatus({
        isTrialActive: true,
        daysRemaining: 999,
        needsUpgrade: false,
        loading: false
      })
      return
    }

    // Calculate trial status based on account creation date
    if (!user?.created_at) {
      console.error('No user created_at timestamp available')
      setStatus({
        isTrialActive: false,
        daysRemaining: 0,
        needsUpgrade: true,
        loading: false
      })
      return
    }

    const createdAt = new Date(user.created_at)
    const now = new Date()
    const daysElapsed = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24))
    const daysRemaining = Math.max(0, TRIAL_DAYS - daysElapsed)
    const isTrialActive = daysElapsed < TRIAL_DAYS

    const needsUpgrade = !isTrialActive && !isPremium

    setStatus({
      isTrialActive,
      daysRemaining,
      needsUpgrade,
      loading: false
    })

    // Redirect to Stripe checkout if trial expired and no premium
    if (needsUpgrade && options?.redirectToCheckout !== false) {
      console.log('Trial expired, redirecting to checkout...')
      window.location.href = STRIPE_CHECKOUT_URL
    }
  }

  return {
    ...status,
    checkoutUrl: STRIPE_CHECKOUT_URL,
    trialDays: TRIAL_DAYS
  }
}

/**
 * Hook to get trial status without enforcing redirect
 * Useful for showing trial banners/warnings
 */
export function useTrialStatus() {
  return useRequirePremium({ redirectToCheckout: false })
}
